import React, { useEffect } from 'react';

import Icon from 'dpl/common/components/Icon';
import {
  HELP_SERVICE_FEE_PATH,
  LEARNING_CENTER_PAYMENT_PROTECTION_PATH
} from 'dpl/shared/constants/urls';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';

import SelectPaymentTypeLearnMoreDrawer from './SelectPaymentTypeLearnMoreDrawer';
import {
  SELECT_PAYMENT_MODAL_NAME,
  US_BANK_ACCOUNT_PAYMENT_METHOD
} from './constants';

interface PaymentProtectionProvision {
  icon: string;
  title: string;
  subtitle: string;
  content: React.ReactNode;
  helpTitle?: string | null;
  helpIssues?: string[];
  learnMoreLink?: string | null;
}

function paymentProtectionProvisions(
  dogPayment: boolean,
  paymentMethod: string | null | undefined
): PaymentProtectionProvision[] {
  return [
    {
      icon: 'gooddog-shield',
      title: 'Payment Protection',
      subtitle: 'Payment protection if something goes wrong',
      content: (
        <>
          Your payment is protected by{' '}
          <a
            className="underline"
            href={LEARNING_CENTER_PAYMENT_PROTECTION_PATH}
            target="_blank"
            rel="noreferrer"
          >
            Good Dog Payment Protection
          </a>{' '}
          if something goes wrong.
        </>
      ),
      helpTitle: 'Common scenarios we’ve helped resolve',
      helpIssues: [
        'Breeder stops responding to you',
        'Breeder doesn’t deliver the dog you were promised',
        'Breeder decides they no longer want to sell you a dog',
        'Breeder does not honor their refund policies',
        dogPayment &&
          'Breeder does not stand by their health guarantee or contract'
      ].filter(Boolean) as string[],
      learnMoreLink: HELP_SERVICE_FEE_PATH
    },
    {
      icon: 'fetch-headset',
      title: 'Support',
      subtitle: 'Expert support when you need it',
      content:
        'Our team of specialists works with breeders every day and is here to help every step of the way, with guidance from veterinarians.',
      helpTitle: 'Common issues we’ve helped with',
      helpIssues: [
        dogPayment && 'Reviewing veterinary records',
        'Resolving disputes with the breeder',
        dogPayment && 'Breeder decides they no longer want to sell you a dog',
        'Logistical and transportation assistance',
        dogPayment && 'Help with behavioral issues',
        'Guidance on puppy care'
      ].filter(Boolean) as string[],
      learnMoreLink: HELP_SERVICE_FEE_PATH
    },
    dogPayment && {
      icon: 'fetch-marketing-training',
      title: 'Puppy Guidance',
      subtitle: 'The right advice, right when you need it',
      content:
        'Everything owners wish they’d known, timed to your puppy’s stage, from pickup through the teenage months.',
      helpTitle: 'What you’ll get',
      helpIssues: [
        'Getting ready and bringing your puppy home',
        'Vet visits, vaccines, and everyday health',
        'Feeding and nutrition',
        'Socialization and building confidence',
        'House training and basic manners',
        'Live coaching from our puppy trainer'
      ]
    },
    Boolean(paymentMethod) &&
      paymentMethod !== US_BANK_ACCOUNT_PAYMENT_METHOD && {
        icon: 'fetch-credit-card-shield',
        title: 'Payment Processing',
        subtitle: 'Processes your payment through Good Dog',
        content:
          'Covers the cost of secure payment processing. Pay by bank transfer (ACH) to skip the processing fee.'
      }
  ].filter(Boolean) as PaymentProtectionProvision[];
}

const CHANGE_PAYMENT_BUTTON_CTA = 'Change payment type to friends & family';

interface SelectPaymentTypeLearnMoreProps {
  dogPayment: boolean;
  onGoBack?: (() => void) | null | false;
  onChangeModal?: ((modalName: string) => void) | null;
  paymentMethod?: string | null;
}

export default function SelectPaymentTypeLearnMore({
  onGoBack = null,
  dogPayment,
  onChangeModal = null,
  paymentMethod = null
}: SelectPaymentTypeLearnMoreProps) {
  useEffect(() => {
    sendAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_MODAL_VIEWED_BY_BUYER, {
      view: ANALYTICS_VIEWS.PROTECTION_SUPPORT_LEARN_MORE,
      view_feature: ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE
    });
  }, []);

  function handleChangePaymentClick() {
    onChangeModal?.(SELECT_PAYMENT_MODAL_NAME.PAYMENT_PROTECTION);
    sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: CHANGE_PAYMENT_BUTTON_CTA,
      view: ANALYTICS_VIEWS.BUYER_PAYMENT_SUMMARY
    });
  }

  return (
    <div className="SelectPaymentTypeLearnMore flex-auto overflow-y-auto no-scrollbars pv4 ph4 pv8-sm ph16-sm">
      {onGoBack && (
        <button type="button" onClick={onGoBack}>
          <Icon
            name="fat-arrow-left"
            className="dib"
            height="30px"
            width="30px"
          />
        </button>
      )}
      <h4 className="font-24 md:font-32 tc mb8 lh-title mt6-sm">
        What’s included in Processing & Protection?
      </h4>
      <div>
        {paymentProtectionProvisions(dogPayment, paymentMethod).map(
          provision => (
            <div key={provision.title} className="mb4">
              <SelectPaymentTypeLearnMoreDrawer {...provision} />
            </div>
          )
        )}
      </div>
      <button
        className="flex justify-center w-100 font-16 fw-medium secondary pv4 mt6 mt8-sm"
        type="button"
        onClick={handleChangePaymentClick}
      >
        {CHANGE_PAYMENT_BUTTON_CTA}
      </button>
    </div>
  );
}
